import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, KeyRound, LogOut, Save, Trash2 } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV } from "@/lib/nav";
import { safeAuth } from "@/lib/auth-errors";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, type NotificationPrefs } from "@/lib/auth";

const DEFAULT_PREFS: NotificationPrefs = {
  email_enquiries: true,
  email_listing_updates: true,
  email_contact_requests: true,
  email_marketing: false,
  sms_enquiries: false,
  sms_listing_updates: false,
  sms_contact_requests: false,
};

export const Route = createFileRoute("/_authenticated/customer/settings")({
  head: () => ({
    meta: [
      { title: "Settings · Landoo" },
      {
        name: "description",
        content: "Manage password, notification preferences and account on Landoo.",
      },
    ],
  }),
  component: CustomerSettings,
});

function CustomerSettings() {
  const { profile, signOut, refreshProfile } = useAuth();
  const nav = useNavigate();

  const [prefs, setPrefs] = useState<NotificationPrefs>(
    profile?.notification_prefs ?? DEFAULT_PREFS,
  );
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    if (profile?.notification_prefs) setPrefs(profile.notification_prefs);
  }, [profile?.notification_prefs]);

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  const [reason, setReason] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [submittingDelete, setSubmittingDelete] = useState(false);

  const savePrefs = async () => {
    if (!profile) return;
    setSavingPrefs(true);
    const { error } = await supabase
      .from("profiles")
      .update({ notification_prefs: prefs as unknown as Record<string, boolean> })
      .eq("id", profile.id);
    setSavingPrefs(false);
    if (error) return toast.error(error.message);
    toast.success("Notification preferences saved");
    await refreshProfile();
  };

  const changePassword = async () => {
    if (pw1.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setChangingPw(true);
    const { error } = await safeAuth(() => supabase.auth.updateUser({ password: pw1 }));
    setChangingPw(false);
    if (error) return toast.error(error);
    setPw1("");
    setPw2("");
    toast.success("Password updated");
  };

  const requestDelete = async () => {
    if (!profile) return;
    setSubmittingDelete(true);
    const { error } = await supabase
      .from("account_deletion_requests")
      .insert({ user_id: profile.id, reason: reason || null });
    if (!error) {
      await supabase
        .from("profiles")
        .update({ deletion_requested_at: new Date().toISOString() })
        .eq("id", profile.id);
    }
    setSubmittingDelete(false);
    if (error) return toast.error(error.message);
    setConfirmDelete(false);
    setReason("");
    toast.success("Deletion request submitted. Our team will contact you shortly.");
    await refreshProfile();
  };

  const doSignOut = async () => {
    await signOut();
    nav({ to: "/login", replace: true });
  };

  return (
    <DashboardShell title="Settings" role="Customer" items={CUSTOMER_NAV}>
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <header className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-coral" />
            <h2 className="text-sm font-semibold text-brown-dark">Change Password</h2>
          </header>
          <label className="block">
            <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
              New password
            </div>
            <input
              type="password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className={inp}
              placeholder="At least 8 characters"
            />
          </label>
          <label className="block">
            <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
              Confirm new password
            </div>
            <input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className={inp}
            />
          </label>
          <button
            onClick={changePassword}
            disabled={changingPw || !pw1}
            className="inline-flex items-center gap-2 rounded-xl bg-coral px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {changingPw ? "Updating…" : "Update password"}
          </button>
        </section>

        <section className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <header>
            <h2 className="text-sm font-semibold text-brown-dark">Notification Preferences</h2>
            <p className="text-xs text-muted-foreground">
              Email delivery is enabled. SMS delivery is a placeholder — settings will apply once
              SMS is live.
            </p>
          </header>
          <PrefGroup label="Email">
            <PrefToggle
              label="New enquiry replies"
              checked={prefs.email_enquiries}
              onChange={(v) => setPrefs({ ...prefs, email_enquiries: v })}
            />
            <PrefToggle
              label="Listing status updates"
              checked={prefs.email_listing_updates}
              onChange={(v) => setPrefs({ ...prefs, email_listing_updates: v })}
            />
            <PrefToggle
              label="Contact request updates"
              checked={prefs.email_contact_requests}
              onChange={(v) => setPrefs({ ...prefs, email_contact_requests: v })}
            />
            <PrefToggle
              label="Marketing & tips"
              checked={prefs.email_marketing}
              onChange={(v) => setPrefs({ ...prefs, email_marketing: v })}
            />
          </PrefGroup>
          <PrefGroup label="SMS (coming soon)">
            <PrefToggle
              label="New enquiry replies"
              checked={prefs.sms_enquiries}
              onChange={(v) => setPrefs({ ...prefs, sms_enquiries: v })}
            />
            <PrefToggle
              label="Listing status updates"
              checked={prefs.sms_listing_updates}
              onChange={(v) => setPrefs({ ...prefs, sms_listing_updates: v })}
            />
            <PrefToggle
              label="Contact request updates"
              checked={prefs.sms_contact_requests}
              onChange={(v) => setPrefs({ ...prefs, sms_contact_requests: v })}
            />
          </PrefGroup>
          <button
            onClick={savePrefs}
            disabled={savingPrefs}
            className="inline-flex items-center gap-2 rounded-xl bg-brown-dark px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {savingPrefs ? "Saving…" : "Save preferences"}
          </button>
        </section>

        <section className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-soft lg:col-span-2">
          <header className="flex items-center gap-2">
            <LogOut className="h-4 w-4 text-coral" />
            <h2 className="text-sm font-semibold text-brown-dark">Logout</h2>
          </header>
          <p className="text-xs text-muted-foreground">Sign out of Landoo on this device.</p>
          <button
            onClick={doSignOut}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-cream px-4 py-2 text-sm font-semibold text-brown-dark hover:bg-cream/70"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </section>

        <section className="space-y-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-5 shadow-soft lg:col-span-2">
          <header className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <h2 className="text-sm font-semibold text-destructive">Delete Account Request</h2>
          </header>
          <p className="text-xs text-muted-foreground">
            Submit a request to permanently delete your Landoo account and data. Our team will
            review and confirm within 7 days.
          </p>
          {profile?.notification_prefs &&
          (profile as unknown as { deletion_requested_at?: string | null })
            .deletion_requested_at ? (
            <p className="text-xs font-medium text-destructive">
              A deletion request is already on file. We will be in touch.
            </p>
          ) : null}
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-destructive/40 bg-white px-4 py-2 text-sm font-semibold text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" /> Request account deletion
            </button>
          ) : (
            <div className="space-y-3">
              <label className="block">
                <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  Reason (optional)
                </div>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className={inp}
                  placeholder="Help us improve"
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={requestDelete}
                  disabled={submittingDelete}
                  className="inline-flex items-center gap-2 rounded-xl bg-destructive px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />{" "}
                  {submittingDelete ? "Submitting…" : "Confirm deletion request"}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-xl border border-border bg-card px-4 py-2 text-sm font-semibold text-brown-dark"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </DashboardShell>
  );
}

function PrefGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-background p-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function PrefToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-cream/50">
      <span className="text-sm text-brown-dark">{label}</span>
      <span className="relative inline-block">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span className="block h-5 w-9 rounded-full bg-border transition peer-checked:bg-coral" />
        <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition peer-checked:translate-x-4" />
      </span>
    </label>
  );
}

const inp =
  "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30";
